import { LocalePrefix } from '@tmh-website/shared/const';
import { Badge, CategoryInterface, DeliveryTime, Maybe, ProductInterface } from '@tmh-website/shared/graphql/graphql';
import { GraphQLClient } from 'graphql-request';

// Strapi has two envs. One for the magento api and one for the interal route.
const MAGENTO_GRAPHQL_API_URL =
  process.env.NEXT_PUBLIC_MAGENTO_GRAPHQL_API_URL ??
  process.env.STRAPI_ADMIN_MAGENTO_GRAPHQL_API_URL ??
  process.env.NX_MAGENTO_GRAPHQL_API_URL;
const MAGENTO_GRAPHQL_API_AUTHORIZATION_HEADER =
  process.env.NEXT_PUBLIC_MAGENTO_GRAPHQL_API_AUTHORIZATION_HEADER ??
  process.env.STRAPI_ADMIN_MAGENTO_GRAPHQL_API_AUTHORIZATION_HEADER ??
  process.env.NX_MAGENTO_GRAPHQL_API_AUTHORIZATION_HEADER;
const isUsingProxy =
  process.env.APP === 'cms' ||
  process.env.IS_LOCAL === 'true' ||
  process.env.STRAPI_ADMIN_IS_LOCAL === 'true' ||
  process.env.NEXT_PUBLIC_IS_LOCAL === 'true';

const magentoGraphQLClients: Record<LocalePrefix, GraphQLClient | undefined> = {
  [LocalePrefix.De]: undefined,
  [LocalePrefix.DeCH]: undefined,
  [LocalePrefix.En]: undefined,
  [LocalePrefix.EnUS]: undefined,
  [LocalePrefix.Fr]: undefined,
  [LocalePrefix.FrCH]: undefined,
};

export function getMagentoGraphQLClient(locale: LocalePrefix): GraphQLClient {
  if (!magentoGraphQLClients[locale]) {
    magentoGraphQLClients[locale] = new GraphQLClient(MAGENTO_GRAPHQL_API_URL!.replace('{{locale}}', locale), {
      headers: {
        /**
         * During development, we use the `X-Magento-Authorization` header to pass the authorization token to the Magento
         * proxy running through the cms app to avoid CORS errors.
         * We cannot use the regular "Authorization" header because it would conflict with Strapi's permissions layer.
         */
        [isUsingProxy ? 'X-Magento-Authorization' : 'Authorization']: MAGENTO_GRAPHQL_API_AUTHORIZATION_HEADER!,
      },
    });
  }
  return magentoGraphQLClients[locale]!;
}

export function getStoreHeaders(locale: LocalePrefix) {
  return {
    Store: locale,
    'Content-Currency': localeToCurrencyMap[locale],
  };
}

export const localeToCurrencyMap: Record<LocalePrefix, string> = {
  [LocalePrefix.En]: 'EUR',
  [LocalePrefix.De]: 'EUR',
  [LocalePrefix.DeCH]: 'CHF',
  [LocalePrefix.EnUS]: 'USD',
  [LocalePrefix.FrCH]: 'CHF',
  [LocalePrefix.Fr]: 'EUR',
};

export interface Product {
  uid: string;
  sku: string;
  name: string;
  manufacturer?: number;
  description?: string;
  imgSrc?: string;
  imgAlt?: string;
  url: string;
  rating?: number;
  reviewsCount?: number;
  price: {
    regular: number;
    reduced?: number;
    discount?: number;
  };
  deliveryTime?: {
    name: DeliveryTime['name'];
    type: DeliveryTime['type'];
  };
  badges: Badge[];
  // We only keep the first 5 categories
  categories?: string[];
}

// We need this type to merge differences between e.g. regular and bundled products
type ProductInputs = Partial<
  Pick<
    ProductInterface,
    | 'uid'
    | 'sku'
    | 'name'
    | 'manufacturer'
    | 'product_sub_name'
    | 'small_image'
    | 'url_key'
    | 'url_suffix'
    | 'review_count'
    | 'rating_summary'
    | 'price_range'
    | 'delivery_time_state'
    | 'badges'
  >
> & { categories?: Maybe<Array<Maybe<Partial<CategoryInterface>>>> };

export function getProductPriceObject(price_range: ProductInterface['price_range']): Product['price'] {
  const price: Product['price'] = { regular: price_range?.minimum_price?.regular_price?.value ?? 0 };
  if (price_range?.minimum_price?.final_price?.value) {
    price.reduced = price_range.minimum_price.final_price.value;
  }
  if (price_range?.minimum_price?.discount?.percent_off) {
    price.discount = price_range.minimum_price.discount.percent_off;
  }
  return price;
}

export function transformGraphQlProductToProduct(graphQlProduct: ProductInputs): Product {
  const product: Product = {
    uid: graphQlProduct.uid ?? '',
    sku: graphQlProduct.sku ?? '',
    name: graphQlProduct.name ?? '',
    url: (graphQlProduct.url_key ?? '') + (graphQlProduct.url_suffix ?? ''),
    price: getProductPriceObject(graphQlProduct.price_range!),
    badges: (graphQlProduct.badges ?? []).filter(badge => !!badge) as Badge[],
  };

  // Optional values have to be added like this so that they are not passed as e.g. `rating: undefined`
  if (graphQlProduct.manufacturer) {
    product.manufacturer = graphQlProduct.manufacturer;
  }
  if (graphQlProduct.product_sub_name) {
    product.description = graphQlProduct.product_sub_name;
  }
  if (graphQlProduct.small_image?.url) {
    product.imgSrc = graphQlProduct.small_image.url;
  }
  if (graphQlProduct.small_image?.label) {
    product.imgAlt = graphQlProduct.small_image.label;
  }
  if (graphQlProduct.rating_summary && graphQlProduct.rating_summary > 0) {
    product.rating = graphQlProduct.rating_summary;
  }
  if (graphQlProduct.review_count && graphQlProduct.review_count > 0) {
    product.reviewsCount = graphQlProduct.review_count;
  }
  if (graphQlProduct.delivery_time_state) {
    product.deliveryTime = {
      name: graphQlProduct.delivery_time_state.name,
      type: graphQlProduct.delivery_time_state.type,
    };
  }

  // Add the first 5 categories
  if (graphQlProduct.categories) {
    product.categories = graphQlProduct.categories.slice(0, 5).map(category => category!.name!);
  }

  return product;
}
